import { useEffect, useRef } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { useNotificationContext } from "@/contexts/useNotificationContext";
import { canAcceptQrOrders } from "@/rbac/actionPermissions";
import type { Notification } from "@/types/notification";
import {
  followUpToastId,
  forgetFollowUpToast,
  openTableInOrders,
  qrFollowUpTarget,
  resetQrFollowUpToasts,
  trackFollowUpToast,
} from "@/lib/qrFollowUp";
import { useStaffConnectionKey } from "@/lib/staffConnection";
import { kotLabel } from "@/lib/orderKots";
import { demoPrintingOff } from "@/lib/tps";

/**
 * Renders sonner toasts for high/urgent notifications received via WebSocket.
 * Mount this once inside the provider tree and the router (an "Open table"
 * action navigates).
 */
export function NotificationToasts() {
  const { realtimeQueue } = useNotificationContext();
  const navigate = useNavigate();
  const shownIds = useRef(new Set<string>());

  // Switch Outlet/Staff or sign-out: the last person's alerts go with them.
  const connectionKey = useStaffConnectionKey();
  const lastConnectionKey = useRef(connectionKey);
  useEffect(() => {
    if (lastConnectionKey.current === connectionKey) return;
    lastConnectionKey.current = connectionKey;
    toast.dismiss();
    resetQrFollowUpToasts();
    printFailedGroups.clear();
    shownIds.current.clear();
  }, [connectionKey]);

  useEffect(() => {
    // Oldest first, so a table's latest follow-up is the one left showing.
    for (const n of [...realtimeQueue].reverse()) {
      if (shownIds.current.has(n.notification_id)) continue;
      // A new QR order is the Accept prompt's (QRAcceptPrompts) for whoever can accept it.
      if (n.notification_type === "qr_order_pending" && canAcceptQrOrders()) {
        shownIds.current.add(n.notification_id);
        continue;
      }
      if (n.priority === "high" || n.priority === "urgent") {
        showToast(n, navigate);
        shownIds.current.add(n.notification_id);
      }
    }

    // Prevent memory leak — only keep IDs present in the queue
    const queueIds = new Set(realtimeQueue.map((n) => n.notification_id));
    for (const id of shownIds.current) {
      if (!queueIds.has(id)) shownIds.current.delete(id);
    }
  }, [realtimeQueue, navigate]);

  return null;
}

/** print_failed toasts on screen: their KOT labels, by order and reason. */
const printFailedGroups = new Map<string, Set<string>>();

interface PrintFailedMeta {
  kind?: "kot" | "bill";
  reason?: string;
  order_id?: string;
  order_number?: string | null;
  table_number?: string | null;
  kot_number?: string | null;
}

/**
 * A KOT or bill no till printed: it stays until closed. The KOTs of one send
 * (same order, same reason) share one toast that lists them ("KOT 3, KOT 4
 * for Table 7 didn't print"), instead of one sticky toast each.
 */
/** Print-failure toasts on screen at most (the oldest goes): they stacked over the POS. */
const MAX_PRINT_FAILED_TOASTS = 3;

function showPrintFailed(n: Notification) {
  // A demo outlet with printing off on this device: nothing was meant to print (QA M20).
  if (demoPrintingOff()) return;
  const meta = (n.metadata ?? {}) as PrintFailedMeta;
  const id = `print-failed:${meta.kind ?? ""}:${meta.order_id ?? n.notification_id}:${meta.reason ?? ""}`;
  if (!printFailedGroups.has(id)) {
    while (printFailedGroups.size >= MAX_PRINT_FAILED_TOASTS) {
      const oldest = printFailedGroups.keys().next().value as string;
      printFailedGroups.delete(oldest);
      toast.dismiss(oldest);
    }
  }
  const labels = printFailedGroups.get(id) ?? new Set<string>();
  if (meta.kind === "kot" && meta.kot_number) labels.add(kotLabel(meta.kot_number));
  printFailedGroups.set(id, labels);
  let title = n.title;
  if (labels.size > 1) {
    const list = [...labels].join(", ");
    const where = meta.table_number ? `Table ${meta.table_number}` : `order ${meta.order_number ?? ""}`.trim();
    title = meta.reason === "no_till_online" ? `No till is online to print ${list}` : `${list} for ${where} didn't print`;
  }
  const forget = () => printFailedGroups.delete(id);
  toast(title, {
    id,
    description: n.body || undefined,
    duration: Infinity,
    dismissible: true,
    onDismiss: forget,
    onAutoClose: forget,
  });
}

function showToast(n: Notification, navigate: NavigateFunction) {
  if (n.notification_type === "print_failed") {
    showPrintFailed(n);
    return;
  }
  const isUrgent = n.priority === "urgent";
  // An auto-accepted QR order (not sent to the kitchen, maybe no waiter), one
  // the auto-accept could not add, or a guest calling a waiter or asking for the
  // bill: staff must go to the table, so it stays until handled. Orders: one per
  // table; guest requests: one per table and type, never replacing the table's
  // order toast. At most three of each kind at once (followUpToastId, trackFollowUpToast).
  const table = qrFollowUpTarget(n);

  if (table) {
    const id = followUpToastId(n, table);
    // trackFollowUpToast already forgot the ones it hands back (a programmatic dismiss has no callback).
    for (const oldest of trackFollowUpToast(id)) toast.dismiss(oldest);
    toast(n.title, {
      id,
      description: n.body || undefined,
      duration: Infinity,
      dismissible: true,
      // Sonner fires neither onDismiss nor onAutoClose for an action click or toast.dismiss().
      action: {
        label: "Open table",
        onClick: () => {
          forgetFollowUpToast(id);
          openTableInOrders(navigate, table);
        },
      },
      onDismiss: () => forgetFollowUpToast(id),
      onAutoClose: () => forgetFollowUpToast(id),
    });
    return;
  }

  toast(n.title, {
    description: n.body || undefined,
    // Urgent toasts stay until dismissed. (sonner 1.7 has no `important`
    // option; it was silently ignored.)
    duration: isUrgent ? Infinity : 8000,
    dismissible: true,
  });
}
